import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import ViewSelector from "../components/ViewSelector";
import KanbanView from "../components/KanbanView";
import CardsView from "../components/CardsView";
import ListView from "../components/ListView";
import {
  CalendarDays, Plus, Loader2, Eye, Trash2, Check, X, Clock,
  CheckCircle, XCircle, AlertCircle, Users, Calendar, ChevronLeft, ChevronRight,
  Package, Send, Search, History, PlusCircle, MinusCircle, CalendarCheck, CalendarX, CalendarPlus,
  Download, FileSpreadsheet, FileText as FileTextIcon, PenTool, AlertTriangle, Pencil
} from "lucide-react";

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", icon: Clock },
  { value: "aprobada", label: "Aprobada", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle },
  { value: "rechazada", label: "Rechazada", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300", icon: XCircle }
];

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// KPI Stats component
const StatsCards = ({ solicitudes, activeFilter, onFilterChange }) => {
  const stats = [
    { key: "all", label: "Total", count: solicitudes.length, icon: Package, color: "blue" },
    { key: "pendiente", label: "Pendientes", count: solicitudes.filter(s => s.estado === "pendiente").length, icon: Clock, color: "amber" },
    { key: "aprobada", label: "Aprobadas", count: solicitudes.filter(s => s.estado === "aprobada").length, icon: CheckCircle, color: "green" },
    { key: "rechazada", label: "Rechazadas", count: solicitudes.filter(s => s.estado === "rechazada").length, icon: XCircle, color: "red" },
  ];

  const colorClasses = {
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400",
    green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {stats.map(stat => {
        const Icon = stat.icon;
        const isActive = activeFilter === stat.key;
        return (
          <Card
            key={stat.key}
            className={`cursor-pointer transition-all hover:shadow-md dark:bg-slate-800 dark:border-slate-700 ${isActive ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => onFilterChange(stat.key)}
            data-testid={`stat-${stat.key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses[stat.color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.count}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default function VacacionesPage() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [calendario, setCalendario] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("solicitudes");
  const [currentView, setCurrentView] = useState("table");
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showSaldoDialog, setShowSaldoDialog] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [editingSolicitud, setEditingSolicitud] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [saldoCompleto, setSaldoCompleto] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  
  // Calendar state
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  
  // Form state
  const [formData, setFormData] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    dias_solicitados: 0,
    motivo: ""
  });
  
  // Saldo form state
  const [saldoForm, setSaldoForm] = useState({
    user_id: "",
    year: new Date().getFullYear(),
    dias_totales: 15,
    dias_usados: 0
  });
  
  // Firma digital state
  const [userFirma, setUserFirma] = useState(null);
  const [firmaSolicitud, setFirmaSolicitud] = useState(false);
  
  // Selection state for bulk delete
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [permisosResumen, setPermisosResumen] = useState(null);
  
  const { getAuthHeaders, user } = useAuth();
  const { solicitudId: urlSolicitudId } = useParams();
  const isAdmin = ["admin", "administrador", "gestion_humana"].includes(user?.role?.toLowerCase());

  // Get user info for display
  const userDepartamento = user?.departamento_nombre || "";
  const userSupervisor = usuarios.find(u => u.id === user?.supervisor_id)?.name || "";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [solRes, saldosRes, calRes, usersRes] = await Promise.all([
        axios.get(`${API}/vacaciones/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/vacaciones/saldos?year=${calYear}`, getAuthHeaders()),
        axios.get(`${API}/vacaciones/calendario?year=${calYear}`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders())
      ]);
      setSolicitudes(solRes.data);
      setSaldos(saldosRes.data);
      setCalendario(calRes.data);
      setUsuarios(usersRes.data);
      
      // Fetch complete balance for current user (including pending days)
      if (user?.id) {
        try {
          const [saldoCompletoRes, permisosRes] = await Promise.all([
            axios.get(`${API}/vacaciones/saldo-completo/${user.id}`, getAuthHeaders()),
            axios.get(`${API}/permisos/mi-resumen`, getAuthHeaders())
          ]);
          setSaldoCompleto(saldoCompletoRes.data);
          setPermisosResumen(permisosRes.data);
        } catch (e) {
          console.log("Could not fetch complete balance or permisos");
        }
      }
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, calYear, user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-open solicitud from URL param (from email link)
  useEffect(() => {
    if (urlSolicitudId && !loading && solicitudes.length > 0) {
      const sol = solicitudes.find(s => s.id === urlSolicitudId);
      if (sol) {
        setSelectedSolicitud(sol);
        setShowViewDialog(true);
      }
    }
  }, [urlSolicitudId, loading, solicitudes]);


  // Load user's signature
  useEffect(() => {
    const loadUserFirma = async () => {
      try {
        const res = await axios.get(`${API}/firma-digital/mi-firma`, getAuthHeaders());
        setUserFirma(res.data?.firma_url || null);
      } catch (err) {
        setUserFirma(null);
      }
    };
    loadUserFirma();
  }, [getAuthHeaders]);

  // Fetch historial when "mi_historial" tab is selected
  const fetchHistorial = useCallback(async () => {
    if (!user?.id) return;
    setLoadingHistorial(true);
    try {
      const res = await axios.get(`${API}/vacaciones/historial/${user.id}`, getAuthHeaders());
      setHistorial(res.data.historial || []);
    } catch (err) {
      console.error("Error fetching historial:", err);
      toast.error("Error al cargar historial");
    } finally {
      setLoadingHistorial(false);
    }
  }, [getAuthHeaders, user?.id]);

  useEffect(() => {
    if (activeTab === "mi_historial") {
      fetchHistorial();
    }
  }, [activeTab, fetchHistorial]);

  // Export historial
  const handleExportHistorial = async (format) => {
    if (!user?.id) return;
    try {
      toast.info(`Generando reporte ${format.toUpperCase()}...`);
      const response = await axios.get(
        `${API}/vacaciones/historial/${user.id}/exportar?format=${format}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historial_vacaciones.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Error al exportar historial");
    }
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Estado para días hábiles calculados
  const [diasHabilesInfo, setDiasHabilesInfo] = useState(null);
  const [calculandoDias, setCalculandoDias] = useState(false);

  const handleDateChange = async (field, value) => {
    const newData = { ...formData, [field]: value };
    
    if (newData.fecha_inicio && newData.fecha_fin) {
      // Calcular días calendario para referencia
      const diasCalendario = calculateDays(newData.fecha_inicio, newData.fecha_fin);
      
      // Llamar al endpoint para calcular días hábiles
      setCalculandoDias(true);
      try {
        const response = await axios.get(
          `${API}/dias-festivos/calcular-dias-habiles?fecha_inicio=${newData.fecha_inicio}&fecha_fin=${newData.fecha_fin}`,
          getAuthHeaders()
        );
        const diasHabiles = response.data.dias_habiles || diasCalendario;
        newData.dias_solicitados = diasHabiles;
        setDiasHabilesInfo({
          dias_calendario: response.data.dias_totales,
          dias_habiles: response.data.dias_habiles,
          festivos: response.data.festivos_en_rango || 0,
          fines_semana: response.data.dias_totales - response.data.dias_habiles - (response.data.festivos_en_rango || 0)
        });
      } catch (err) {
        console.error("Error calculando días hábiles:", err);
        // Fallback a cálculo local si falla el endpoint
        newData.dias_solicitados = diasCalendario;
        setDiasHabilesInfo(null);
      } finally {
        setCalculandoDias(false);
      }
    } else {
      newData.dias_solicitados = 0;
      setDiasHabilesInfo(null);
    }
    
    setFormData(newData);
  };

  const openEditDialog = (sol) => {
    setEditingSolicitud(sol);
    setFormData({
      fecha_inicio: sol.fecha_inicio,
      fecha_fin: sol.fecha_fin,
      dias_solicitados: sol.dias_solicitados,
      motivo: sol.motivo || ""
    });
    setDiasHabilesInfo(null);
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.fecha_inicio || !formData.fecha_fin) {
      toast.error("Seleccione fechas de inicio y fin");
      return;
    }
    
    // Edit mode: update existing pending request
    if (editingSolicitud) {
      setSaving(true);
      try {
        await axios.put(`${API}/vacaciones/solicitudes/${editingSolicitud.id}`, formData, getAuthHeaders());
        toast.success("Solicitud actualizada. Se ha notificado a su supervisor con las fechas actualizadas.");
        setShowDialog(false);
        setEditingSolicitud(null);
        setFormData({ fecha_inicio: "", fecha_fin: "", dias_solicitados: 0, motivo: "" });
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al modificar solicitud");
      } finally {
        setSaving(false);
      }
      return;
    }
    
    // Check if user has signed the request
    if (!firmaSolicitud) {
      toast.error("Debe firmar la solicitud antes de enviarla");
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/vacaciones/solicitudes`, formData, getAuthHeaders());
      const solicitudId = res.data?.id;
      
      // Sign the request after creation
      if (solicitudId && userFirma) {
        await axios.post(`${API}/firma-digital/firmar-solicitud`, {
          solicitud_id: solicitudId,
          tipo_solicitud: "vacacion",
          usar_firma_guardada: true
        }, getAuthHeaders());
      }
      
      if (res.data?.es_adelanto) {
        toast.warning(
          `Su solicitud incluye ${res.data.dias_adelantados} día(s) POR ADELANTADO de vacaciones futuras (disponibles: ${res.data.dias_disponibles_al_solicitar}). ` +
          `Requiere aprobación de su supervisor Y de Gestión Humana.`,
          { duration: 10000 }
        );
      } else {
        toast.success("Solicitud creada y firmada exitosamente. Se ha notificado a su supervisor.");
      }
      setShowDialog(false);
      setFormData({ fecha_inicio: "", fecha_fin: "", dias_solicitados: 0, motivo: "" });
      setFirmaSolicitud(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear solicitud");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (solicitudId, newStatus, comentarios = "") => {
    try {
      const res = await axios.put(
        `${API}/vacaciones/solicitudes/${solicitudId}/estado?estado=${newStatus}${comentarios ? `&comentarios=${encodeURIComponent(comentarios)}` : ""}`,
        {},
        getAuthHeaders()
      );
      if (res.data?.mensaje) {
        toast.info(res.data.mensaje, { duration: 9000 });
      } else {
        toast.success(`Solicitud ${newStatus === "aprobada" ? "aprobada" : "rechazada"}. Se han enviado las notificaciones.`);
      }
      setShowViewDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const handleDelete = async (solicitudId) => {
    if (!confirm("¿Está seguro de eliminar esta solicitud?")) return;
    try {
      await axios.delete(`${API}/vacaciones/solicitudes/${solicitudId}`, getAuthHeaders());
      toast.success("Solicitud eliminada");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleSaveSaldo = async () => {
    if (!saldoForm.user_id) {
      toast.error("Seleccione un usuario");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/vacaciones/saldos`, saldoForm, getAuthHeaders());
      toast.success("Saldo actualizado");
      setShowSaldoDialog(false);
      fetchData();
    } catch (err) {
      toast.error("Error al guardar saldo");
    } finally {
      setSaving(false);
    }
  };

  const getEstado = (estado) => ESTADOS.find(e => e.value === estado) || ESTADOS[0];

  // Selection functions for bulk delete
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredSolicitudes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSolicitudes.map(s => s.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!deletePassword || selectedIds.length === 0) {
      toast.error("Ingrese su contraseña para confirmar");
      return;
    }
    
    setDeleting(true);
    try {
      const res = await axios.post(
        `${API}/vacaciones/solicitudes/bulk-delete`,
        {
          ids: selectedIds,
          password: deletePassword
        },
        getAuthHeaders()
      );
      
      toast.success(`${res.data.deleted || selectedIds.length} solicitudes eliminadas`);
      setSelectedIds([]);
      setShowBulkDeleteDialog(false);
      setDeletePassword("");
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al eliminar solicitudes";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  // Filtering
  let filteredSolicitudes = solicitudes;
  if (statusFilter !== "all") {
    filteredSolicitudes = filteredSolicitudes.filter(s => s.estado === statusFilter);
  }
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredSolicitudes = filteredSolicitudes.filter(s =>
      s.user_name?.toLowerCase().includes(query) ||
      s.departamento_nombre?.toLowerCase().includes(query) ||
      s.motivo?.toLowerCase().includes(query)
    );
  }

  // Calendar helpers
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const getEventsForDay = (day) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return calendario.filter(event => {
      return dateStr >= event.start && dateStr <= event.end;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calYear, calMonth);
    const firstDay = getFirstDayOfMonth(calYear, calMonth);
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const events = getEventsForDay(day);
      days.push(
        <div key={day} className="h-24 border border-slate-200 dark:border-slate-700 p-1 overflow-hidden">
          <div className="font-medium text-sm text-slate-600 dark:text-slate-400">{day}</div>
          <div className="space-y-0.5 mt-1">
            {events.slice(0, 2).map((event, idx) => (
              <div key={idx} className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1 py-0.5 rounded truncate">
                {event.title}
              </div>
            ))}
            {events.length > 2 && (
              <div className="text-xs text-slate-500">+{events.length - 2} más</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  // Get user's own balance
  const myBalance = saldos.find(s => s.user_id === user?.id);

  // Prepare data for different views
  const kanbanColumns = ESTADOS.map(e => ({
    id: e.value,
    title: e.label,
    items: filteredSolicitudes.filter(s => s.estado === e.value).map(s => ({
      id: s.id,
      title: s.user_name || "Usuario",
      subtitle: `${s.fecha_inicio} - ${s.fecha_fin}`,
      badge: `${s.dias_solicitados} días`,
      status: e.value
    }))
  }));

  const cardItems = filteredSolicitudes.map(s => {
    const estado = getEstado(s.estado);
    return {
      id: s.id,
      title: s.user_name || "Usuario",
      subtitle: s.departamento_nombre || "",
      description: s.motivo || "",
      badge: estado.label,
      badgeColor: estado.color,
      meta: `${s.fecha_inicio} - ${s.fecha_fin} (${s.dias_solicitados} días)`
    };
  });

  const listItems = filteredSolicitudes.map(s => {
    const estado = getEstado(s.estado);
    return {
      id: s.id,
      title: s.user_name || "Usuario",
      subtitle: `${s.fecha_inicio} - ${s.fecha_fin}`,
      badge: estado.label,
      badgeColor: estado.color
    };
  });

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Gestión de Vacaciones
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Control de solicitudes y saldos de vacaciones
            </p>
          </div>
          <Button onClick={() => { setEditingSolicitud(null); setFormData({ fecha_inicio: "", fecha_fin: "", dias_solicitados: 0, motivo: "" }); setDiasHabilesInfo(null); setShowDialog(true); }} className="gap-2" data-testid="new-vacation-btn">
            <Plus className="w-4 h-4" /> Nueva Solicitud
          </Button>
        </div>

        {/* My Balance Card - Enhanced with pending days and alerts */}
        {(myBalance || saldoCompleto) && (
          <div className="mb-6 space-y-3">
            <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Mi Saldo Total de Vacaciones</p>
                    <p className="text-3xl font-bold">
                      {saldoCompleto?.total_disponible ?? myBalance?.dias_disponibles ?? 0} días disponibles
                    </p>
                  </div>
                  <div className="text-right text-blue-100 space-y-1">
                    {saldoCompleto?.total_pendientes_anteriores > 0 && (
                      <p className="bg-white/20 px-2 py-1 rounded text-sm">
                        Pendientes años anteriores: {saldoCompleto.total_pendientes_anteriores} días
                      </p>
                    )}
                    <p>Año actual ({calYear}): {saldoCompleto?.dias_año_actual ?? myBalance?.dias_disponibles ?? 0} días</p>
                    {myBalance && <p className="text-xs">Total: {myBalance.dias_totales} | Usados: {myBalance.dias_usados}</p>}
                  </div>
                </div>
                
                {/* Show pending years breakdown */}
                {saldoCompleto?.pendientes_años_anteriores?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <p className="text-xs text-blue-100 mb-2">Desglose de días pendientes (se descontarán primero los más antiguos):</p>
                    <div className="flex flex-wrap gap-2">
                      {saldoCompleto.pendientes_años_anteriores.map(p => (
                        <span key={p.id} className="bg-white/20 px-2 py-1 rounded text-xs">
                          {p.year}: {p.dias_restantes} días
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Alert for low balance */}
            {saldoCompleto?.alerta && (
              <Card className="bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700">
                <CardContent className="py-3">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    <div>
                      <p className="font-medium text-amber-700 dark:text-amber-300">{saldoCompleto.alerta.mensaje}</p>
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        Considere planificar sus vacaciones con anticipación.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Permisos Announcement Banner */}
        {permisosResumen && (
          <Card className="mb-6 border-0 bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-md" data-testid="permisos-banner">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 rounded-full p-2.5">
                    <CalendarCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-violet-100 text-sm">Mi Saldo de Permisos ({permisosResumen.year})</p>
                    <p className="text-2xl font-bold">{permisosResumen.cantidad_disponible} permisos disponibles</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-right text-violet-100">
                  <div>
                    <p className="text-xs">Total asignados</p>
                    <p className="text-lg font-semibold text-white">{permisosResumen.cantidad_total}</p>
                  </div>
                  <div className="w-px h-10 bg-white/20" />
                  <div>
                    <p className="text-xs">Usados</p>
                    <p className="text-lg font-semibold text-white">{permisosResumen.cantidad_usada}</p>
                  </div>
                  {permisosResumen.pendientes > 0 && (
                    <>
                      <div className="w-px h-10 bg-white/20" />
                      <div>
                        <p className="text-xs">Pendientes</p>
                        <Badge className="bg-white/25 hover:bg-white/25 text-white border-0 text-base font-bold">
                          {permisosResumen.pendientes}
                        </Badge>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="solicitudes" className="gap-2">
              <Clock className="w-4 h-4" /> Solicitudes
            </TabsTrigger>
            <TabsTrigger value="mi_historial" className="gap-2">
              <History className="w-4 h-4" /> Mi Historial
            </TabsTrigger>
            {(isAdmin || user?.supervisor_id !== undefined) && (
              <TabsTrigger value="saldos" className="gap-2">
                <Users className="w-4 h-4" /> Saldos
              </TabsTrigger>
            )}
            <TabsTrigger value="calendario" className="gap-2">
              <Calendar className="w-4 h-4" /> Calendario
            </TabsTrigger>
          </TabsList>

          {/* Solicitudes Tab */}
          <TabsContent value="solicitudes">
            {/* Stats KPI Cards */}
            <StatsCards
              solicitudes={solicitudes}
              activeFilter={statusFilter}
              onFilterChange={setStatusFilter}
            />

            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre, departamento..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-10 w-64"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {isAdmin && currentView === "table" && filteredSolicitudes.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="select-all-vac"
                        checked={selectedIds.length === filteredSolicitudes.length && filteredSolicitudes.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                      <Label htmlFor="select-all-vac" className="text-sm cursor-pointer">
                        {selectedIds.length === filteredSolicitudes.length ? "Deseleccionar" : "Seleccionar"} todo
                      </Label>
                      {selectedIds.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setShowBulkDeleteDialog(true)}
                          data-testid="bulk-delete-vac-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Eliminar ({selectedIds.length})
                        </Button>
                      )}
                    </div>
                  )}
                  <ViewSelector currentView={currentView} onViewChange={setCurrentView} />
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : filteredSolicitudes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay solicitudes</p>
                  </div>
                ) : currentView === "table" ? (
                  <div className="space-y-3">
                    {filteredSolicitudes.map(sol => {
                      const estado = getEstado(sol.estado);
                      const EstadoIcon = estado.icon;
                      const isSelected = selectedIds.includes(sol.id);
                      return (
                        <div
                          key={sol.id}
                          className={`flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                          data-testid={`vacation-request-${sol.id}`}
                        >
                          <div className="flex items-center gap-4">
                            {isAdmin && (
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleSelect(sol.id)}
                              />
                            )}
                            <div className={`p-2 rounded-full ${estado.color}`}>
                              <EstadoIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900 dark:text-white">
                                  {sol.user_name || "Usuario"}
                                </p>
                                {sol.user_id !== user?.id && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                                    <Users className="w-3 h-3 mr-1" />
                                    Supervisado{sol.created_by_name && sol.created_by_name !== sol.user_name ? `: ${sol.created_by_name}` : ''}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {sol.fecha_inicio} - {sol.fecha_fin} ({sol.dias_solicitados} días)
                              </p>
                              {sol.departamento_nombre && (
                                <p className="text-xs text-slate-400">{sol.departamento_nombre}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {sol.modificada && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700" data-testid={`modified-badge-${sol.id}`}>
                                Modificada
                              </Badge>
                            )}
                            <Badge className={estado.color}>{estado.label}</Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setSelectedSolicitud(sol); setShowViewDialog(true); }}
                              data-testid={`view-vacation-${sol.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {sol.estado === "pendiente" && sol.user_id === user?.id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-500 hover:text-blue-700"
                                onClick={() => openEditDialog(sol)}
                                title="Modificar solicitud"
                                data-testid={`edit-vacation-${sol.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            )}
                            {sol.estado === "pendiente" && (sol.user_id === user?.id || isAdmin) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDelete(sol.id)}
                                data-testid={`delete-vacation-${sol.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : currentView === "kanban" ? (
                  <KanbanView
                    columns={kanbanColumns}
                    onItemClick={(item) => {
                      const sol = solicitudes.find(s => s.id === item.id);
                      if (sol) { setSelectedSolicitud(sol); setShowViewDialog(true); }
                    }}
                  />
                ) : currentView === "cards" ? (
                  <CardsView
                    items={cardItems}
                    onItemClick={(item) => {
                      const sol = solicitudes.find(s => s.id === item.id);
                      if (sol) { setSelectedSolicitud(sol); setShowViewDialog(true); }
                    }}
                  />
                ) : (
                  <ListView
                    items={listItems}
                    onItemClick={(item) => {
                      const sol = solicitudes.find(s => s.id === item.id);
                      if (sol) { setSelectedSolicitud(sol); setShowViewDialog(true); }
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mi Historial Tab */}
          <TabsContent value="mi_historial">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader className="flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5 text-blue-500" />
                    Historial de Cambios en Mi Saldo
                  </CardTitle>
                  <CardDescription>
                    Registro cronológico de todos los movimientos que han afectado tu saldo de vacaciones.
                  </CardDescription>
                </div>
                {historial.length > 0 && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleExportHistorial('excel')}
                      className="gap-2"
                      data-testid="export-excel-btn"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleExportHistorial('pdf')}
                      className="gap-2"
                      data-testid="export-pdf-btn"
                    >
                      <FileTextIcon className="w-4 h-4" />
                      PDF
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {loadingHistorial ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : historial.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay registros en tu historial</p>
                    <p className="text-sm mt-1">Los cambios aparecerán aquí cuando se aprueben solicitudes o se realicen ajustes.</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
                      
                      {/* Timeline items */}
                      <div className="space-y-4">
                        {historial.map((item, idx) => {
                          // Determine icon and colors based on type
                          const getIconAndColor = () => {
                            switch (item.tipo) {
                              case "solicitud_aprobada":
                                return { 
                                  Icon: CalendarCheck, 
                                  bg: "bg-red-100 dark:bg-red-900/30", 
                                  text: "text-red-600 dark:text-red-400",
                                  badge: "bg-red-100 text-red-700"
                                };
                              case "solicitud_rechazada":
                                return { 
                                  Icon: CalendarX, 
                                  bg: "bg-slate-100 dark:bg-slate-700", 
                                  text: "text-slate-500 dark:text-slate-400",
                                  badge: "bg-slate-100 text-slate-600"
                                };
                              case "ajuste":
                                return item.dias > 0 
                                  ? { 
                                      Icon: PlusCircle, 
                                      bg: "bg-green-100 dark:bg-green-900/30", 
                                      text: "text-green-600 dark:text-green-400",
                                      badge: "bg-green-100 text-green-700"
                                    }
                                  : { 
                                      Icon: MinusCircle, 
                                      bg: "bg-orange-100 dark:bg-orange-900/30", 
                                      text: "text-orange-600 dark:text-orange-400",
                                      badge: "bg-orange-100 text-orange-700"
                                    };
                              case "pendientes_agregados":
                                return { 
                                  Icon: History, 
                                  bg: "bg-blue-100 dark:bg-blue-900/30", 
                                  text: "text-blue-600 dark:text-blue-400",
                                  badge: "bg-blue-100 text-blue-700"
                                };
                              case "saldo_inicial":
                                return { 
                                  Icon: CalendarPlus, 
                                  bg: "bg-purple-100 dark:bg-purple-900/30", 
                                  text: "text-purple-600 dark:text-purple-400",
                                  badge: "bg-purple-100 text-purple-700"
                                };
                              default:
                                return { 
                                  Icon: Calendar, 
                                  bg: "bg-slate-100 dark:bg-slate-700", 
                                  text: "text-slate-500",
                                  badge: "bg-slate-100 text-slate-600"
                                };
                            }
                          };
                          
                          const { Icon, bg, text, badge } = getIconAndColor();
                          const fecha = item.fecha ? new Date(item.fecha) : null;
                          
                          return (
                            <div key={item.id || idx} className="relative pl-12" data-testid={`historial-item-${idx}`}>
                              {/* Icon circle */}
                              <div className={`absolute left-0 w-10 h-10 rounded-full ${bg} flex items-center justify-center z-10`}>
                                <Icon className={`w-5 h-5 ${text}`} />
                              </div>
                              
                              {/* Content card */}
                              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 ml-2">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <p className="font-medium text-slate-900 dark:text-white">
                                      {item.descripcion}
                                    </p>
                                    
                                    {/* Details based on type */}
                                    {item.tipo === "solicitud_aprobada" && item.detalles && (
                                      <div className="mt-2 text-sm text-slate-600 dark:text-slate-400 space-y-1">
                                        {item.detalles.aprobado_por && (
                                          <p>Aprobado por: {item.detalles.aprobado_por}</p>
                                        )}
                                        {item.detalles.descuento?.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            <span className="text-xs">Descontado de:</span>
                                            {item.detalles.descuento.map((d, i) => (
                                              <Badge key={i} variant="outline" className="text-xs">
                                                {d.year}: {d.dias_descontados} días
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {item.tipo === "ajuste" && item.detalles && (
                                      <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                        <p>Tipo: {item.detalles.tipo_ajuste}</p>
                                        {item.detalles.realizado_por && (
                                          <p>Por: {item.detalles.realizado_por}</p>
                                        )}
                                        <p className="text-xs mt-1">
                                          Saldo: {item.detalles.saldo_anterior} → {item.detalles.saldo_nuevo} días
                                        </p>
                                      </div>
                                    )}
                                    
                                    {item.tipo === "pendientes_agregados" && item.detalles && (
                                      <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                        {item.detalles.notas && <p>{item.detalles.notas}</p>}
                                        {item.detalles.dias_usados > 0 && (
                                          <p className="text-xs">Ya usados: {item.detalles.dias_usados} días</p>
                                        )}
                                      </div>
                                    )}
                                    
                                    {item.tipo === "saldo_inicial" && item.detalles && (
                                      <div className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                                        <p>Días asignados: {item.detalles.dias_totales}</p>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="text-right flex-shrink-0">
                                    <Badge className={`${badge} ${item.dias !== 0 ? '' : 'opacity-50'}`}>
                                      {item.dias > 0 ? '+' : ''}{item.dias} días
                                    </Badge>
                                    {fecha && (
                                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                        {fecha.toLocaleDateString('es-ES', { 
                                          day: 'numeric', 
                                          month: 'short', 
                                          year: 'numeric' 
                                        })}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saldos Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="saldos">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Saldos de Vacaciones {calYear}</CardTitle>
                  <Button size="sm" onClick={() => setShowSaldoDialog(true)} className="gap-2">
                    <Plus className="w-4 h-4" /> Agregar/Editar Saldo
                  </Button>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : saldos.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay saldos registrados</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {saldos.map(saldo => (
                        <div
                          key={saldo.id}
                          className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                          data-testid={`balance-${saldo.id}`}
                        >
                          <p className="font-medium text-slate-900 dark:text-white">{saldo.user_name}</p>
                          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-2xl font-bold text-blue-600">{saldo.dias_totales}</p>
                              <p className="text-xs text-slate-500">Total</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-amber-600">{saldo.dias_usados}</p>
                              <p className="text-xs text-slate-500">Usados</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-green-600">{saldo.dias_disponibles}</p>
                              <p className="text-xs text-slate-500">Disponibles</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Calendario Tab */}
          <TabsContent value="calendario">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Calendario de Vacaciones</CardTitle>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => {
                    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
                    else setCalMonth(m => m - 1);
                  }}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium w-40 text-center">{MESES[calMonth]} {calYear}</span>
                  <Button size="sm" variant="outline" onClick={() => {
                    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
                    else setCalMonth(m => m + 1);
                  }}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-0">
                  {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(day => (
                    <div key={day} className="p-2 text-center font-medium text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      {day}
                    </div>
                  ))}
                  {renderCalendar()}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* New Request Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="dark:bg-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSolicitud ? "Modificar Solicitud de Vacaciones" : "Nueva Solicitud de Vacaciones"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Read-only user info fields */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-500">Solicitante</Label>
                  <Input value={user?.name || ""} disabled className="bg-slate-100 dark:bg-slate-700" data-testid="solicitante-field" />
                </div>
                <div>
                  <Label className="text-slate-500">Departamento</Label>
                  <Input value={userDepartamento || "No asignado"} disabled className="bg-slate-100 dark:bg-slate-700" data-testid="departamento-field" />
                </div>
                <div>
                  <Label className="text-slate-500">Supervisor</Label>
                  <Input value={userSupervisor || "No asignado"} disabled className="bg-slate-100 dark:bg-slate-700" data-testid="supervisor-field" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Inicio</Label>
                  <Input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={e => handleDateChange("fecha_inicio", e.target.value)}
                    data-testid="vacation-start-date"
                  />
                </div>
                <div>
                  <Label>Fecha Fin</Label>
                  <Input
                    type="date"
                    value={formData.fecha_fin}
                    onChange={e => handleDateChange("fecha_fin", e.target.value)}
                    data-testid="vacation-end-date"
                  />
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <p className="text-blue-700 dark:text-blue-300 font-medium">
                    Días hábiles solicitados: {calculandoDias ? <Loader2 className="w-4 h-4 inline animate-spin" /> : formData.dias_solicitados}
                  </p>
                </div>
                {diasHabilesInfo && (
                  <div className="mt-2 text-sm text-blue-600 dark:text-blue-400 space-y-1">
                    <p>📅 Días calendario: {diasHabilesInfo.dias_calendario}</p>
                    <p>✅ Días hábiles (L-V): {diasHabilesInfo.dias_habiles}</p>
                    {diasHabilesInfo.fines_semana > 0 && (
                      <p>🚫 Fines de semana excluidos: {diasHabilesInfo.fines_semana}</p>
                    )}
                    {diasHabilesInfo.festivos > 0 && (
                      <p>🎉 Días festivos excluidos: {diasHabilesInfo.festivos}</p>
                    )}
                  </div>
                )}
                {saldoCompleto && (
                  <div className="text-sm text-blue-600 dark:text-blue-400 mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                    <p>Total disponible: {saldoCompleto.total_disponible} días</p>
                    {saldoCompleto.total_pendientes_anteriores > 0 && (
                      <p className="text-xs">({saldoCompleto.total_pendientes_anteriores} de años anteriores)</p>
                    )}
                  </div>
                )}
                {!saldoCompleto && myBalance && (
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                    Días disponibles: {myBalance.dias_disponibles}
                  </p>
                )}
              </div>
              <div>
                <Label>Motivo (opcional)</Label>
                <Textarea
                  value={formData.motivo}
                  onChange={e => setFormData({ ...formData, motivo: e.target.value })}
                  placeholder="Describa el motivo de su solicitud"
                  data-testid="vacation-reason"
                />
              </div>
              
              <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                {editingSolicitud
                  ? "Al guardar los cambios, se enviará un nuevo correo a su supervisor con las fechas actualizadas."
                  : "Al guardar, se enviará una notificación a su supervisor para aprobación."}
              </div>

              {/* Firma Digital Section - Required (solo para nuevas solicitudes) */}
              {!editingSolicitud && (
              <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                <Label className="flex items-center gap-2 mb-3 text-slate-700 dark:text-slate-300">
                  <PenTool className="w-4 h-4" />
                  Firma Digital <span className="text-red-500">*</span>
                </Label>
                
                {!userFirma ? (
                  <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Firma no configurada</p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Debe configurar su firma digital antes de enviar solicitudes.
                        Vaya a <strong>Configuración → Firma Digital</strong> para cargar su firma.
                      </p>
                    </div>
                  </div>
                ) : firmaSolicitud ? (
                  <div className="flex items-center gap-4 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                    <img 
                      src={userFirma} 
                      alt="Mi firma" 
                      className="h-12 object-contain border rounded bg-white"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">Solicitud firmada</span>
                      </div>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                        {user?.name} - {new Date().toLocaleDateString()}
                      </p>
                    </div>
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFirmaSolicitud(false)}
                      className="text-slate-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="flex-1 p-3 border border-dashed border-slate-300 dark:border-slate-500 rounded bg-white dark:bg-slate-800 text-center">
                      <p className="text-sm text-slate-500">Haga clic en "Firmar" para agregar su firma</p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => setFirmaSolicitud(true)}
                      className="gap-2 bg-blue-600 hover:bg-blue-700"
                    >
                      <PenTool className="w-4 h-4" />
                      Firmar
                    </Button>
                  </div>
                )}
              </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDialog(false); setFirmaSolicitud(false); setEditingSolicitud(null); }}>Cancelar</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={saving || (!editingSolicitud && (!firmaSolicitud || !userFirma))} 
                data-testid="submit-vacation-btn"
                title={editingSolicitud ? "" : !userFirma ? "Configure su firma primero" : !firmaSolicitud ? "Debe firmar la solicitud" : ""}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {editingSolicitud ? "Guardar Cambios" : "Enviar Solicitud"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View/Approve Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalle de Solicitud</DialogTitle>
            </DialogHeader>
            {selectedSolicitud && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-500">Solicitante</Label>
                    <p className="font-medium">{selectedSolicitud.user_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Departamento</Label>
                    <p className="font-medium">{selectedSolicitud.departamento_nombre || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Supervisor</Label>
                    <p className="font-medium">{selectedSolicitud.supervisor_nombre || "N/A"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-slate-500">Fecha Inicio</Label>
                    <p className="font-medium">{selectedSolicitud.fecha_inicio}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Fecha Fin</Label>
                    <p className="font-medium">{selectedSolicitud.fecha_fin}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Días</Label>
                    <p className="font-medium">{selectedSolicitud.dias_solicitados}</p>
                  </div>
                </div>
                {selectedSolicitud.motivo && (
                  <div>
                    <Label className="text-slate-500">Motivo</Label>
                    <p>{selectedSolicitud.motivo}</p>
                  </div>
                )}
                {selectedSolicitud.es_adelanto && (
                  <div className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-1" data-testid="adelanto-info-box">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">⚠️ Solicitud con días adelantados</p>
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      Toma <strong>{selectedSolicitud.dias_disponibles_al_solicitar ?? 0} día(s)</strong> de sus vacaciones disponibles y{" "}
                      <strong>{selectedSolicitud.dias_adelantados ?? 0} día(s) POR ADELANTADO</strong> de vacaciones futuras.
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Requiere aprobación del supervisor Y de Gestión Humana. Un rechazo declina la solicitud completa.
                    </p>
                    <div className="flex gap-2 pt-1">
                      <Badge className={selectedSolicitud.aprobacion_supervisor === "aprobada" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                        Supervisor: {selectedSolicitud.aprobacion_supervisor === "aprobada" ? "Aprobada" : "Pendiente"}
                      </Badge>
                      <Badge className={selectedSolicitud.aprobacion_gh === "aprobada" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                        Gestión Humana: {selectedSolicitud.aprobacion_gh === "aprobada" ? "Aprobada" : "Pendiente"}
                      </Badge>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-slate-500">Estado</Label>
                  <div className="flex items-center gap-2">
                    <Badge className={getEstado(selectedSolicitud.estado).color}>
                      {getEstado(selectedSolicitud.estado).label}
                    </Badge>
                    {selectedSolicitud.modificada && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700" data-testid="detail-modified-badge">
                        Modificada por el colaborador
                      </Badge>
                    )}
                  </div>
                </div>
                {selectedSolicitud.aprobado_por_nombre && (
                  <div>
                    <Label className="text-slate-500">
                      {selectedSolicitud.estado === "aprobada" ? "Aprobado por" : "Rechazado por"}
                    </Label>
                    <p>{selectedSolicitud.aprobado_por_nombre} - {selectedSolicitud.fecha_aprobacion}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              {selectedSolicitud?.estado === "aprobada" && (isAdmin || selectedSolicitud?.user_id !== user?.id) && (
                <Button
                  variant="outline"
                  className="text-red-500 border-red-500 hover:bg-red-50"
                  onClick={() => {
                    if (confirm("¿Declinar esta solicitud aprobada? Los días descontados serán restaurados y el colaborador podrá crear una nueva solicitud.")) {
                      handleStatusChange(selectedSolicitud.id, "rechazada");
                    }
                  }}
                  data-testid="decline-approved-vacation-btn"
                >
                  <X className="w-4 h-4 mr-2" /> Declinar Solicitud
                </Button>
              )}
              {selectedSolicitud?.estado === "pendiente" && (isAdmin || selectedSolicitud?.user_id !== user?.id) && (
                <>
                  <Button
                    variant="outline"
                    className="text-red-500 border-red-500 hover:bg-red-50"
                    onClick={() => handleStatusChange(selectedSolicitud.id, "rechazada")}
                    data-testid="reject-vacation-btn"
                  >
                    <X className="w-4 h-4 mr-2" /> Rechazar
                  </Button>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleStatusChange(selectedSolicitud.id, "aprobada")}
                    data-testid="approve-vacation-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> Aprobar
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Saldo Dialog */}
        <Dialog open={showSaldoDialog} onOpenChange={setShowSaldoDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Agregar/Editar Saldo de Vacaciones</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Usuario</Label>
                <Select
                  value={saldoForm.user_id}
                  onValueChange={v => setSaldoForm({ ...saldoForm, user_id: v })}
                >
                  <SelectTrigger data-testid="saldo-user-select">
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Año</Label>
                  <Input
                    type="number"
                    value={saldoForm.year}
                    onChange={e => setSaldoForm({ ...saldoForm, year: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Días Totales</Label>
                  <Input
                    type="number"
                    value={saldoForm.dias_totales}
                    onChange={e => setSaldoForm({ ...saldoForm, dias_totales: parseInt(e.target.value) })}
                    data-testid="saldo-total-days"
                  />
                </div>
                <div>
                  <Label>Días Usados</Label>
                  <Input
                    type="number"
                    value={saldoForm.dias_usados}
                    onChange={e => setSaldoForm({ ...saldoForm, dias_usados: parseInt(e.target.value) })}
                    data-testid="saldo-used-days"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSaldoDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveSaldo} disabled={saving} data-testid="save-saldo-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Confirmar Eliminación Masiva
              </DialogTitle>
              <DialogDescription>
                Está a punto de eliminar <strong>{selectedIds.length}</strong> solicitudes de vacaciones de forma permanente.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      Esta acción es IRREVERSIBLE
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Las solicitudes seleccionadas serán eliminadas permanentemente y los saldos podrían verse afectados.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-password-vac">Ingrese su contraseña para confirmar</Label>
                <Input
                  id="delete-password-vac"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && handleBulkDelete()}
                  data-testid="delete-password-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowBulkDeleteDialog(false);
                  setDeletePassword("");
                }}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button 
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={deleting || !deletePassword}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedIds.length} solicitudes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
